// Sign-out after a day without activity (#284, owner decision 2026-10-10):
// anyone who uses the app at least once a day stays signed in, up to the
// 30-day refresh-token maximum; a session left unused for 24 h ends.
//
// Activity (a click, a tap or a key) is stamped in localStorage, at most once a
// minute, so every tab shares one clock. A session with no stamp yet (signed
// in before this shipped) starts its clock now rather than being signed out.

export const IDLE_LIMIT_MS = 24 * 60 * 60 * 1000;
// How often a signed-in tab checks; the limit is a day, so minutes are plenty.
export const IDLE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const STAMP_EVERY_MS = 60 * 1000;
const KEY = 'basiqr:last-activity';

export function lastActivity(): number | null {
  try {
    const value = Number(localStorage.getItem(KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

/** Stamp activity now; skipped when stamped less than a minute ago, unless forced. */
export function recordActivity(now = Date.now(), force = false): void {
  const last = lastActivity();
  if (!force && last !== null && now - last < STAMP_EVERY_MS) return;
  try {
    localStorage.setItem(KEY, String(now));
  } catch {
    /* storage unavailable: no idle sign-out for this browser */
  }
}

/** True when the last stamped activity is more than IDLE_LIMIT_MS ago. */
export function isIdleExpired(now = Date.now()): boolean {
  const last = lastActivity();
  return last !== null && now - last > IDLE_LIMIT_MS;
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;

/** Stamp activity on user input; returns the cleanup. */
export function trackActivity(): () => void {
  const onActivity = () => recordActivity();
  for (const event of ACTIVITY_EVENTS) window.addEventListener(event, onActivity, { passive: true });
  return () => {
    for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, onActivity);
  };
}
